/**
 * ICU/cEEG reporting cadence: first review SLA, 24h reports,
 * twice-daily reminders, and significant-change triggers.
 */

export type CeegCadenceConfig = {
  firstReviewMinutesMin: number;
  firstReviewMinutesMax: number;
  reportCadenceHours: number;
  twiceDailyReminderHours: [number, number];
};

export const DEFAULT_CEEG_CADENCE: CeegCadenceConfig = {
  firstReviewMinutesMin: 30,
  firstReviewMinutesMax: 60,
  reportCadenceHours: 24,
  twiceDailyReminderHours: [8, 20],
};

export type CeegCadenceStatus = {
  firstReviewDueAtIso: string;
  firstReviewOverdue: boolean;
  next24hReportDueAtIso: string;
  next24hReportOverdue: boolean;
  nextTwiceDailyReminderAtIso: string;
  significantChangeTriggered: boolean;
  reminders: string[];
};

function addMinutes(isoOrDate: Date, minutes: number): Date {
  return new Date(isoOrDate.getTime() + minutes * 60_000);
}

function addHours(isoOrDate: Date, hours: number): Date {
  return new Date(isoOrDate.getTime() + hours * 3_600_000);
}

function nextTwiceDailyReminder(
  now: Date,
  hours: [number, number],
): Date {
  const candidates = hours.map((hour) => {
    const d = new Date(now);
    d.setHours(hour, 0, 0, 0);
    if (d.getTime() <= now.getTime()) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  });
  candidates.sort((a, b) => a.getTime() - b.getTime());
  return candidates[0];
}

export function computeCeegCadenceStatus(input: {
  monitoringStartedAt: Date | string;
  now?: Date | string;
  lastReportAt?: Date | string | null;
  firstReviewCompletedAt?: Date | string | null;
  significantChange?: boolean;
  config?: Partial<CeegCadenceConfig>;
}): CeegCadenceStatus {
  const config = { ...DEFAULT_CEEG_CADENCE, ...(input.config || {}) };
  const started =
    input.monitoringStartedAt instanceof Date
      ? input.monitoringStartedAt
      : new Date(input.monitoringStartedAt);
  const now =
    input.now instanceof Date
      ? input.now
      : input.now
        ? new Date(input.now)
        : new Date();
  const firstReviewDue = addMinutes(started, config.firstReviewMinutesMax);
  const firstReviewDone = Boolean(input.firstReviewCompletedAt);
  const firstReviewOverdue = !firstReviewDone && now > firstReviewDue;

  const lastReport = input.lastReportAt
    ? input.lastReportAt instanceof Date
      ? input.lastReportAt
      : new Date(input.lastReportAt)
    : started;
  const next24h = addHours(lastReport, config.reportCadenceHours);
  const next24hOverdue = now > next24h;
  const significantChangeTriggered = Boolean(input.significantChange);

  const reminders: string[] = [];
  if (!firstReviewDone) {
    reminders.push(
      `Complete first review within ${config.firstReviewMinutesMin}-${config.firstReviewMinutesMax} minutes of monitoring start`,
    );
  }
  if (next24hOverdue) {
    reminders.push("24-hour cEEG report cadence is overdue");
  } else {
    reminders.push("Next 24-hour cEEG report due");
  }
  reminders.push("Twice-daily clinical update reminder");
  if (significantChangeTriggered) {
    reminders.push("Significant-change report trigger active");
  }

  return {
    firstReviewDueAtIso: firstReviewDue.toISOString(),
    firstReviewOverdue,
    next24hReportDueAtIso: next24h.toISOString(),
    next24hReportOverdue: next24hOverdue,
    nextTwiceDailyReminderAtIso: nextTwiceDailyReminder(
      now,
      config.twiceDailyReminderHours,
    ).toISOString(),
    significantChangeTriggered,
    reminders,
  };
}
